import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ArrowRight, BookOpen, Star, Users, TrendingUp } from 'lucide-react';

interface DiscoverTourProps {
  onComplete: () => void;
}

const tourSteps = [
  {
    title: "Welcome to Discover! 📚",
    description: "Let's take a quick tour to help you find amazing books and connect with readers like you.",
    icon: BookOpen,
  },
  {
    title: "Similar Readers",
    description: "Once you rate books, we'll show you readers with similar taste. The more you rate, the better your matches!",
    icon: Users,
  },
  {
    title: "Trending Books",
    description: "See what the community is loving right now. Click any book to view details and add your own rating.",
    icon: TrendingUp,
  },
  {
    title: "Rate Your First Books",
    description: "Head to your profile or search for books you've read. Rating just a few books will unlock personalized recommendations!",
    icon: Star,
  },
];

export function DiscoverTour({ onComplete }: DiscoverTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [open, setOpen] = useState(true);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    setOpen(false);
    onComplete();
  };

  const step = tourSteps[currentStep];
  const Icon = step.icon;
  const progress = ((currentStep + 1) / tourSteps.length) * 100;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mb-4 flex justify-center">
            <div className="rounded-full bg-primary/10 p-4">
              <Icon className="h-8 w-8 text-primary" />
            </div>
          </div>
          <DialogTitle className="text-center text-2xl">{step.title}</DialogTitle>
          <DialogDescription className="text-center text-base pt-2">
            {step.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Step {currentStep + 1} of {tourSteps.length}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={handleSkip} className="w-full sm:w-auto">
            Skip Tour
          </Button>
          <Button onClick={handleNext} className="w-full sm:w-auto">
            {currentStep < tourSteps.length - 1 ? (
              <>
                Next
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            ) : (
              "Get Started"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
